/**
 * Lazy Loading Fallback Component
 * Provides loading states for lazy-loaded components
 */

import React from 'react';

interface LazyLoadingFallbackProps {
  message?: string;
  size?: 'small' | 'medium' | 'large';
  fullScreen?: boolean;
}

const LazyLoadingFallback: React.FC<LazyLoadingFallbackProps> = ({ 
  message = 'Loading...', 
  size = 'medium',
  fullScreen = false 
}) => {
  const sizeClasses = {
    small: 'h-6 w-6',
    medium: 'h-12 w-12',
    large: 'h-16 w-16'
  };

  const textSizeClasses = {
    small: 'text-sm',
    medium: 'text-lg',
    large: 'text-xl'
  };

  const containerClasses = fullScreen 
    ? 'min-h-screen flex items-center justify-center bg-gradient-to-br from-[#e3f2fd] to-[#f1f8e9]'
    : 'flex items-center justify-center p-8';

  return (
    <div 
      className={containerClasses}
      data-ui-id="lazy-loading-fallback"
      data-ui-props={JSON.stringify({
        type: 'loadingFallback',
        component: 'LazyLoadingFallback',
        message: message,
        size: size,
        fullScreen: fullScreen
      })}
    >
      <div className="text-center">
        <div 
          className={`animate-spin rounded-full border-b-2 border-[#1565c0] mx-auto mb-4 ${sizeClasses[size]}`}
          data-ui-id="loading-spinner"
          data-ui-props={JSON.stringify({
            type: 'spinner',
            component: 'LazyLoadingFallback',
            size: size,
            color: '#1565c0'
          })}
        ></div>
        <p 
          className={`text-[#666] dark:text-gray-400 ${textSizeClasses[size]}`}
          data-ui-id="loading-message"
          data-ui-props={JSON.stringify({
            type: 'message',
            component: 'LazyLoadingFallback',
            message: message,
            size: size,
            color: '#666'
          })}
        >
          {message}
        </p>
      </div>
    </div>
  );
};

// Specialized loading components for different use cases
export const RouteLoadingFallback: React.FC = () => (
  <LazyLoadingFallback 
    message="Loading page..." 
    size="large" 
    fullScreen={true}
  />
);

export const ComponentLoadingFallback: React.FC = () => (
  <LazyLoadingFallback 
    message="Loading component..." 
    size="medium" 
  />
);

export const ModalLoadingFallback: React.FC = () => (
  <LazyLoadingFallback 
    message="Loading modal..." 
    size="small" 
  />
);

export const ChartLoadingFallback: React.FC = () => (
  <LazyLoadingFallback 
    message="Loading chart..." 
    size="medium" 
  />
);

export const TableLoadingFallback: React.FC = () => (
  <LazyLoadingFallback 
    message="Loading table..." 
    size="medium" 
  />
);

export default LazyLoadingFallback;